/*
  Mélora media gallery
  Custom element <melora-gallery>. The slider itself is a native scroll-snap
  container (so touch swipe is smooth for free); this script keeps the thumbnails,
  dots and arrows in sync with it, and follows variant changes.
*/
(function () {
  if (customElements.get('melora-gallery')) return;

  class MeloraGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.mg__track');
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('.mg__slide'));
      this.thumbs = Array.from(this.querySelectorAll('.mg__thumb'));
      this.dots = Array.from(this.querySelectorAll('.mg__dot'));
      this.thumbList = this.querySelector('.mg__thumb-list');
      this.prevBtn = this.querySelector('[data-mg-prev]');
      this.nextBtn = this.querySelector('[data-mg-next]');

      this.index = 0;
      this.lock = false;
      this.ticking = false;
      this.targetLeft = 0;
      this.ready = false;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onKey = this.onKey.bind(this);

      // If the visitor touches / drags / wheels the slider mid-animation, they
      // take over: stop ignoring scroll events.
      this.releaseLock = () => {
        if (!this.lock) return;
        this.lock = false;
        clearTimeout(this.lockTimer);
      };

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKey);
      ['pointerdown', 'touchstart', 'wheel'].forEach((type) => {
        this.track.addEventListener(type, this.releaseLock, { passive: true });
      });
      window.addEventListener('resize', this.onResize);

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));
      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i)));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

      var initial = this.dataset.initialMedia;
      if (initial) this.goToMedia(initial, false);
      this.update(this.index);

      this.subscribeToVariantChange();
      this.preloadWhenIdle();
    }

    disconnectedCallback() {
      if (this.track) {
        this.track.removeEventListener('scroll', this.onScroll);
        this.track.removeEventListener('keydown', this.onKey);
        ['pointerdown', 'touchstart', 'wheel'].forEach((type) => {
          this.track.removeEventListener(type, this.releaseLock);
        });
      }
      window.removeEventListener('resize', this.onResize);
      clearTimeout(this.lockTimer);
      if (typeof this.unsubscribeVariant === 'function') this.unsubscribeVariant();
    }

    /* ---------- navigation ---------- */

    step(direction) {
      this.goTo(this.index + direction);
    }

    goTo(index, animate) {
      if (animate === undefined) animate = true;
      var max = this.slides.length - 1;
      var i = Math.max(0, Math.min(max, index));
      var left = i * this.track.clientWidth;

      this.preload(i);

      if (animate && !this.reduceMotion.matches) {
        // While the smooth scroll is running, ignore scroll events so the active
        // thumbnail doesn't flicker through every slide it passes.
        this.lock = true;
        this.targetLeft = left;
        clearTimeout(this.lockTimer);
        this.lockTimer = setTimeout(() => {
          this.lock = false;
          this.syncFromScroll();
        }, 900);
        this.track.scrollTo({ left: left, behavior: 'smooth' });
      } else {
        this.lock = false;
        var previous = this.track.style.scrollBehavior;
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = left;
        this.track.style.scrollBehavior = previous;
      }

      this.update(i);
    }

    goToMedia(mediaId, animate) {
      var id = String(mediaId);
      var i = this.slides.findIndex(function (slide) {
        return slide.dataset.mediaId === id;
      });
      if (i > -1) this.goTo(i, animate);
    }

    // Compatibility with Dawn's older variant code, which calls
    // setActiveMedia('<sectionId>-<mediaId>').
    setActiveMedia(mediaId) {
      this.goToMedia(String(mediaId).split('-').pop());
    }

    /* ---------- state ---------- */

    update(i) {
      var changed = i !== this.index || !this.ready;
      this.index = i;
      this.ready = true;

      this.thumbs.forEach(function (thumb, k) {
        var on = k === i;
        thumb.classList.toggle('is-active', on);
        if (on) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      this.dots.forEach(function (dot, k) {
        var on = k === i;
        dot.classList.toggle('is-active', on);
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (this.prevBtn) this.prevBtn.disabled = i <= 0;
      if (this.nextBtn) this.nextBtn.disabled = i >= this.slides.length - 1;

      if (changed) {
        this.pauseOtherVideos(i);
        this.centerThumb(i);
      }
    }

    syncFromScroll() {
      var width = this.track.clientWidth || 1;
      var position = this.track.scrollLeft;

      if (this.lock) {
        if (Math.abs(position - this.targetLeft) < 2) this.lock = false;
        else return;
      }

      this.update(Math.round(position / width));
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.syncFromScroll();
      });
    }

    onResize() {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        var previous = this.track.style.scrollBehavior;
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = this.index * this.track.clientWidth;
        this.track.style.scrollBehavior = previous;
      });
    }

    onKey(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.slides.length - 1);
      }
    }

    /* ---------- helpers ---------- */

    // Keep the active thumbnail visible inside the thumbnail strip without
    // scrolling the whole page (scrollIntoView would do that).
    centerThumb(i) {
      var list = this.thumbList;
      var thumb = this.thumbs[i];
      if (!list || !thumb) return;
      var behavior = this.reduceMotion.matches ? 'auto' : 'smooth';

      if (list.scrollHeight > list.clientHeight + 1) {
        list.scrollTo({
          top: thumb.offsetTop - (list.clientHeight - thumb.offsetHeight) / 2,
          behavior: behavior
        });
      }
      if (list.scrollWidth > list.clientWidth + 1) {
        list.scrollTo({
          left: thumb.offsetLeft - (list.clientWidth - thumb.offsetWidth) / 2,
          behavior: behavior
        });
      }
    }

    pauseOtherVideos(activeIndex) {
      this.slides.forEach(function (slide, k) {
        if (k === activeIndex) return;
        slide.querySelectorAll('video').forEach(function (video) {
          if (!video.paused) video.pause();
        });
      });
    }

    // Lazy images sit off-screen inside the scroller, so load them ahead of the
    // click/swipe to avoid a blank frame mid-slide.
    preload(i) {
      var slide = this.slides[i];
      var img = slide && slide.querySelector('img[loading="lazy"]');
      if (img) img.setAttribute('loading', 'eager');
    }

    preloadWhenIdle() {
      var run = () => {
        this.slides.forEach((slide, k) => this.preload(k));
      };
      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 3000 });
      else setTimeout(run, 1500);
    }

    // Follow variant changes (Dawn publishes PUB_SUB_EVENTS.variantChange).
    subscribeToVariantChange() {
      if (typeof subscribe !== 'function') return;
      if (typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.variantChange) return;

      this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        var data = event && event.data;
        if (!data || data.sectionId !== this.dataset.section) return;
        var media = data.variant && data.variant.featured_media;
        if (media && media.id) this.goToMedia(media.id);
      });
    }
  }

  customElements.define('melora-gallery', MeloraGallery);
})();
